import Component from 'vue-class-component'
import FeedbackComponent from '@/components/base/FeedbackComponent'
import {FieldType, SearchFieldModel} from '@payroll/search-results-widget/src/models/SearchField.model'
import {GridColumn} from '@payroll/grid-widget/src/models/GridColumn'
import {Action, Getter, Mutation} from 'vuex-class'
import {StoreNamespace} from '@/globals/StoreNamespaceGlobal'
import {MonitoringUserModel} from '@/models/MonitoringUser.model'
import {KeyValuePair, Notification, PortalType, ServiceApi} from '@payroll/optimus-common'

@Component({})
export default class UsersMonitoringComponent extends FeedbackComponent {
  private readonly getPortalTypesPath: string = 'getPortalTypes'

  public PortalType = PortalType

  @Action('getPortalTypes', StoreNamespace.SEARCH) getPortalTypes!: any
  @Mutation('setPortalTypes', StoreNamespace.SEARCH) setPortalTypes!: any
  @Getter('portalTypes', StoreNamespace.SEARCH) portalTypes!: KeyValuePair[]

  private searchLoginFields: SearchFieldModel[] = []
  private columns: GridColumn[] = []

  private showUserEditModal: boolean = false
  public secUserRecord: MonitoringUserModel = {} as MonitoringUserModel
  public secUserRecords: MonitoringUserModel[] = []
  private previousSearchObject: any = {}
  private statusTypes: KeyValuePair[] = []

  /* variables to handle the 'unlock user' button at the table */
  private showUnlockUserModal: boolean = false

  mounted() {
    this.statusTypes = [{
      value: 'true',
      description: this.$t('success').toString()
    }, {
      value: 'false',
      description: this.$t('fail').toString()
    }]

    this.getPortalTypes({requestorCallback: this})

    this.searchLoginFields.push({
      label: 'id_num',
      property: 'idNumber',
      fieldType: FieldType.TEXT_FIELD,
      maxLength: 100,
      bootstrapColumn: 2,
      isRequired: true
    })

    if (this.portalTypes.length === 0) {
      return
    }
    this.searchLoginFields.push({
      label: 'user_portal_type',
      property: 'portalType',
      fieldType: FieldType.SELECT_FIELD,
      selectOptions: [...this.portalTypes, {value: 4, description: this.$t(PortalType.BACKOFFICE).toString()}],
      unSelectFirst: true,
      bootstrapColumn: 2
    })

    this.searchLoginFields.push({
      label: 'success_flag',
      property: 'status',
      fieldType: FieldType.SELECT_FIELD,
      selectOptions: this.statusTypes,
      unSelectFirst: true,
      bootstrapColumn: 2
    })

    this.searchLoginFields.push({
      label: 'start_date',
      property: 'startDate',
      fieldType: FieldType.DATE_FIELD,
      bootstrapColumn: 3
    })
    this.searchLoginFields.push({
      label: 'end_date',
      property: 'endDate',
      fieldType: FieldType.DATE_FIELD,
      bootstrapColumn: 3
    })
    this.initHistoryColumns()
  }

  private initHistoryColumns(): void {
    this.columns.push({property: 'status', header: 'success_flag'})
    this.columns.push({property: 'auditDate', header: 'audit_date'})
    this.columns.push({property: 'portalType', header: 'portal_type'})

    this.columns.push({property: 'loginFailureMsg', header: 'login_failure_msg'})
    this.columns.push({property: 'entry', header: 'mobile_flag'})
    this.columns.push({property: 'ipAddress', header: 'ip_address'})
    this.columns.push({property: 'userAgent', header: 'user_agent'})
  }

  public loadLogins(searchObject: any) {
    this.previousSearchObject = searchObject
    this.secUserRecords = []
    new ServiceApi()
      .setToken(this.accessToken)
      .setRequestName('getUserLoginAudits')
      .setRequestBody(searchObject)
      .post(this)
  }

  onSuccess(data?: any, requestName?: any, message?: string) {
    switch (requestName) {
      case this.getPortalTypesPath:
        this.setPortalTypes(data)
        break
      case 'getUserLoginAudits':
        this.secUserRecords = data.secUserRecords
        data.secLoginAuditRecords.results.forEach((result: any) => {
          result.entry = "<div><div class='device-icon " +
            (result.entry === 'MOBILE' ? 'icon-mobile' : 'icon-desktop') + "'></div>" +
            "<div class='device-text'>" +
            this.$t(result.entry === 'MOBILE' ? 'mobile' : 'desktop') + "</div>" +
            "</div>"

          result.status = "<div><div class='status-icon " +
            (result.status === 'SUCCESS' ? 'icon-check' : 'icon-fail') + "'></div>" +
            "<div class='status-text'>" +
            this.$t(result.status === 'SUCCESS' ? 'success' : 'fail') + "</div>" +
            "</div>"
        })
        this.$emit('successResults', data.secLoginAuditRecords)
        break
      case 'updateUserDetails':
      case 'updateMember':
        if (this.previousSearchObject.pageSize) {
          this.loadLogins(this.previousSearchObject)
        }
        const notificationObj: Notification = {
          toShow: true,
          message: 'data_update_success',
          icon: "checkCircle",
          secondsToDisappear: 3
        }
        this.$root.$emit('showAlertNotification', notificationObj)
        this.showUserEditModal = false
        break

      case 'unlockUser/' + this.secUserRecord.secUserKey :
        const unlockNotification: Notification = {
          toShow: true,
          message: 'unlock_success',
          icon: "checkCircle",
          secondsToDisappear: 3
        }
        this.$root.$emit('showAlertNotification', unlockNotification)
        this.showUnlockUserModal = false
        break
    }
  }

  onError(messages: string[], code: number, requestName?: string): void {
    switch (requestName) {
      case 'unlockUser/' + this.secUserRecord.secUserKey :
        const unlockNotification: Notification = {
          toShow: true,
          message: messages && messages.length > 0 ? messages[0] : 'unlock_failed',
          icon: 'warning',
          secondsToDisappear: 3
        }
        this.$root.$emit('showAlertNotification', unlockNotification)
        this.showUnlockUserModal = false
        break
      default:
        const notificationObj: Notification = {
          toShow: true,
          message: messages && messages.length > 0 ? messages[0] : 'general_error_msg',
          icon: 'warning',
          secondsToDisappear: 3
        }
        this.$root.$emit('showAlertNotification', notificationObj)
        break
    }
  }

  editUserDetails(user: MonitoringUserModel) {
    this.showUserEditModal = true
    this.secUserRecord.secUserKey = user.secUserKey
    this.secUserRecord.memberKey = user.memberKey
    this.secUserRecord.email = user.email
    this.secUserRecord.phoneNum = user.phoneNum
  }

  unlockUser(user: MonitoringUserModel) {
    this.secUserRecord.fullName = user.fullName
    this.secUserRecord.secUserKey = user.secUserKey
    this.showUnlockUserModal = true
  }

  confirmUnlock(user: MonitoringUserModel) {
    new ServiceApi()
      .setToken(this.accessToken)
      .setRequestName('unlockUser/' + this.secUserRecord.secUserKey)
      .post(this)

  }

  public onUserUpdate(): any {
    new ServiceApi()
      .setToken(this.accessToken)
      .setPathVariable(this.secUserRecord.secUserKey || this.secUserRecord.memberKey || '')
      .setRequestName(this.secUserRecord.secUserKey ? 'updateUserDetails' : 'updateMember')
      .setRequestBody(this.secUserRecord)
      .put(this)
  }

  public onClearSearch() {
    this.secUserRecords = []
  }
}
